import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, MessageSquare, Download, Clock, Users, Utensils } from "lucide-react";
import { useSavedRecipes, SavedRecipe } from "@/hooks/useSavedRecipes";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import { NutritionIcons } from "./NutritionIcons";
import { RecipeDetailModal } from "./RecipeDetailModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const RecipeBrowser = () => {
  const { recipes, loading, deleteRecipe, exportShoppingList, importRecipeAsMeal } = useSavedRecipes();
  const navigate = useNavigate();
  const [selectedRecipes, setSelectedRecipes] = useState<string[]>([]);
  const [recipeToDelete, setRecipeToDelete] = useState<string | null>(null);
  const [selectedRecipeDetail, setSelectedRecipeDetail] = useState<SavedRecipe | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const toggleRecipeSelection = (recipeId: string) => {
    setSelectedRecipes(prev => 
      prev.includes(recipeId) 
        ? prev.filter(id => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  const handleExportShoppingList = () => {
    if (selectedRecipes.length === 0) {
      return;
    }
    exportShoppingList(selectedRecipes);
  };

  const handleDeleteRecipe = async () => {
    if (!recipeToDelete) return;
    await deleteRecipe(recipeToDelete);
    setRecipeToDelete(null);
    setSelectedRecipes(prev => prev.filter(id => id !== recipeToDelete));
  };

  const handleRecipeClick = (recipe: SavedRecipe) => {
    setSelectedRecipeDetail(recipe);
    setShowDetailModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-xl sm:text-2xl font-normal">AI Recipes</h2>
          <p className="text-muted-foreground text-sm">AI-generated fitness recipes tailored to your goals</p>
        </div>
        <div className="flex gap-2">
          {selectedRecipes.length > 0 && (
            <Button
              onClick={handleExportShoppingList}
              variant="outline"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Shopping List ({selectedRecipes.length})
            </Button>
          )}
          <Button
            onClick={() => navigate('/chat')}
            className="bg-gradient-primary text-black gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            Ask Coach
          </Button>
        </div>
      </div>

      {/* Recipes Grid */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground mt-2">Loading recipes...</p>
        </div>
      ) : recipes.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="h-12 w-12 mx-auto text-primary mb-4" />
          <h3 className="text-xl font-normal mb-2">No Recipes Yet</h3>
          <p className="text-muted-foreground mb-6">
            Ask your AI coach to generate personalized recipes based on your fitness goals!<br />
            For example: "Give me 5 high-protein meal prep recipes with at least 50g protein and max 700 calories"
          </p>
          <Button
            onClick={() => navigate('/chat')}
            className="bg-gradient-primary"
          >
            Chat with Coach
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {recipes.map((recipe) => {
            const totalTime = (recipe.prep_time_minutes || 0) + (recipe.cook_time_minutes || 0);
            
            return (
              <Card key={recipe.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardContent className="p-4 cursor-pointer" onClick={() => handleRecipeClick(recipe)}>
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-lg flex-1 line-clamp-2">{recipe.title}</h3>
                    <div className="flex items-center gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedRecipes.includes(recipe.id)}
                        onCheckedChange={() => toggleRecipeSelection(recipe.id)}
                        aria-label="Select for shopping list"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          setRecipeToDelete(recipe.id);
                        }}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {recipe.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                      {recipe.description}
                    </p>
                  )}

                  {/* Macros */}
                  <div className="mb-3 p-3 bg-gradient-card rounded-lg">
                    <NutritionIcons
                      calories={recipe.calories}
                      protein={recipe.protein}
                      carbs={recipe.carbs}
                      fat={recipe.fat}
                      className="flex-wrap justify-center"
                      iconSize={16}
                    />
                  </div>

                  {/* Additional Info */}
                  <div className="flex gap-4 text-xs text-muted-foreground mb-3">
                    {totalTime > 0 && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {totalTime}min
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {recipe.servings} servings
                    </span>
                  </div>

                  {/* Ingredients Preview */}
                   {recipe.ingredients && recipe.ingredients.length > 0 && (
                    <div className="text-xs text-muted-foreground mb-3">
                      <p className="font-medium mb-1">{recipe.ingredients.length} ingredients</p>
                      <p className="line-clamp-2">
                        {recipe.ingredients.slice(0, 3).map(ing => ing.ingredient_name).join(', ')}
                        {recipe.ingredients.length > 3 && '...'}
                      </p>
                    </div>
                  )}

                  {/* Import as Meal */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full gap-2" 
                        size="sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Utensils className="h-4 w-4" />
                        Import as Meal
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="z-50 bg-background border border-border shadow-lg">
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'breakfast')}>
                        Breakfast
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'lunch')}>
                        Lunch
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'dinner')}>
                        Dinner
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => importRecipeAsMeal(recipe.id, 'snack')}>
                        Snack
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!recipeToDelete} onOpenChange={() => setRecipeToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recipe?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the recipe from your collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteRecipe} className="bg-destructive hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Recipe Detail Modal */}
      <RecipeDetailModal
        recipe={selectedRecipeDetail}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedRecipeDetail(null);
        }}
      />
    </div>
  );
};
